/** How each backend error `code` is shown. Every code the backend can return has an entry (a test
 * fails when one is missing); unknown codes still degrade to a generic message plus the raw code.
 *
 * Treatments: `field` beside an input, `alert` inline on the screen, `banner` for "this changed
 * while you were working", `finding` a list of items with fix links, `page` a full-page state
 * (public links and expiry), `toast` a passing confirmation. */
export type Treatment = "field" | "alert" | "banner" | "finding" | "page" | "toast";
export type CodeInfo = { title: string; treatment: Treatment };

export const CODES: Record<string, CodeInfo> = {
  "address.undeliverable": {
    title: "The host's address is undeliverable, so nothing can be sent to it.",
    treatment: "finding",
  },
  copies_only_as_self: {
    title: "Cc and Bcc are only for an email you send as yourself.",
    treatment: "finding",
  },
  too_many_copies: {
    title: "Too many addresses in Cc or Bcc (10 each at most).",
    treatment: "finding",
  },
  copy_address_invalid: {
    title: "A Cc or Bcc entry is not an email address.",
    treatment: "finding",
  },
  copy_is_host: {
    title: "The host already gets this email; take them out of Cc/Bcc.",
    treatment: "finding",
  },
  copy_is_ours: {
    title: "One of our own addresses is in Cc/Bcc; it would mail us back.",
    treatment: "finding",
  },
  copy_is_honoree: {
    title: "An honoree is in Cc/Bcc. Nothing is ever sent to an honoree.",
    treatment: "finding",
  },
  copy_is_vendor: {
    title: "A vendor is in Cc/Bcc before Booked; they and the host would see each other.",
    treatment: "finding",
  },
  accept_by_in_past: {
    title: "The acceptance deadline must be in the future.",
    treatment: "field",
  },
  accept_required: { title: "Please tick that you accept the package.", treatment: "field" },
  approver_cannot_record: {
    title: "You approved the host's request to pay this way, so someone else records the money.",
    treatment: "alert",
  },
  bill_to_required: { title: "Please say who the invoice is for.", treatment: "field" },
  manual_request_declined: {
    title: "We can't take that way of paying for this booking. You can still pay by card.",
    treatment: "alert",
  },
  manual_request_not_pending: {
    title: "This request has already been decided or closed.",
    treatment: "alert",
  },
  manual_request_pending: {
    title: "A request to pay another way is already waiting for this package.",
    treatment: "alert",
  },
  method_unavailable: {
    title: "That way of paying is not available. You can pay by card.",
    treatment: "field",
  },
  already_accepted: {
    title: "This package version has already been accepted.",
    treatment: "alert",
  },
  address_taken: {
    title: "This address already belongs to a different host.",
    treatment: "alert",
  },
  agent_disabled: {
    title: "This agent is switched off. A Reviewer/Admin can switch it on in Configuration.",
    treatment: "alert",
  },
  already_committed: {
    title: "This import was already used to create an event.",
    treatment: "alert",
  },
  candidate_not_found: {
    title: "That shortlisted vendor is no longer on this event.",
    treatment: "alert",
  },
  capabilities_invalid: { title: "Some of these values do not fit the form.", treatment: "field" },
  category_not_on_vendor: { title: "This vendor is not in that category.", treatment: "alert" },
  delivery_mismatch: {
    title: "That delivery is not one of this vendor's service on this event.",
    treatment: "alert",
  },
  event_not_open: { title: "That event is not an open event of this host.", treatment: "alert" },
  host_already_erased: { title: "This host is already erased.", treatment: "alert" },
  host_has_open_events: {
    title: "Archive every event of this host before erasing them.",
    treatment: "alert",
  },
  host_money_moving: {
    title:
      "Money is still moving for this host (a payment, refund, payout or open dispute). Erase them once it settles.",
    treatment: "alert",
  },
  host_email_required: { title: "The host needs an email address.", treatment: "field" },
  import_empty: { title: "There is nothing to read. Paste the text first.", treatment: "field" },
  import_running: {
    title: "The agents are still reading. Create the event once every step has finished.",
    treatment: "alert",
  },
  import_source: { title: "Paste some text or choose an inbox thread.", treatment: "alert" },
  import_too_long: { title: "That is too long to read in one go. Paste less.", treatment: "field" },
  links_invalid: {
    title: "Each link needs a label and a web address starting with http.",
    treatment: "field",
  },
  no_capability_set: {
    title: "This category has no capability form yet. Add one in Configuration.",
    treatment: "alert",
  },
  nothing_to_scout: {
    title: "There is no open delivery of this service to look for.",
    treatment: "alert",
  },
  photo_anonymity_unconfirmed: {
    title: "Confirm the photo shows no name, logo, watermark or contact detail first.",
    treatment: "field",
  },
  photo_invalid: { title: "A photo must be a JPEG or PNG.", treatment: "field" },
  photo_not_found: { title: "That photo no longer exists.", treatment: "alert" },
  photo_too_large: { title: "A photo can be up to 5 MB.", treatment: "field" },
  scout_failed: {
    title: "The scout did not come back with a usable answer. Try again.",
    treatment: "alert",
  },
  site_facts_failed: {
    title:
      "No usable suggestions from the conversation this time. Try again, or type the details in.",
    treatment: "alert",
  },
  step_not_failed: {
    title: "Only a step that failed can be retried.",
    treatment: "alert",
  },
  too_many_deliveries: {
    title: "Find nearby works on up to three deliveries at a time. Pick one.",
    treatment: "alert",
  },
  model_unavailable: {
    title: "The model did not answer. Try again in a minute.",
    treatment: "alert",
  },
  too_many_photos: {
    title: "A vendor can have up to 12 photos. Remove one first.",
    treatment: "alert",
  },
  unknown_category: {
    title: "That service is not one we are set up to source.",
    treatment: "field",
  },
  address_is_honoree: {
    title: "This address belongs to an honoree, who is never contacted. Change their role first.",
    treatment: "alert",
  },
  address_is_reachable: {
    title: "We write to this address for the host, so it cannot belong to an honoree.",
    treatment: "field",
  },
  already_sent: { title: "This update was already emailed to the host.", treatment: "alert" },
  delivery_not_found: { title: "That delivery is no longer in the brief.", treatment: "alert" },
  tracker_closed: {
    title: "This event is closed, so its tracking page is no longer available.",
    treatment: "page",
  },
  update_empty: { title: "Choose a status or write a note for the host.", treatment: "field" },
  update_has_link: { title: "A note for the host cannot hold a link.", treatment: "field" },
  update_leaks_vendor: {
    title: "The note names a vendor, which the host must not see before Booked.",
    treatment: "field",
  },
  recipient_is_honoree: {
    title: "An honoree is never contacted. Remove them from the recipients.",
    treatment: "alert",
  },
  host_default: {
    title: "This is the host's default for every event. Change it for this event instead.",
    treatment: "alert",
  },
  already_paused: { title: "Already paused.", treatment: "alert" },
  amount_invalid: { title: "That amount cannot be refunded.", treatment: "field" },
  amount_required: { title: "Say how much to refund.", treatment: "field" },
  anonymized_required: { title: "Anonymized required.", treatment: "field" },
  attachment_blocked: { title: "Attachment blocked.", treatment: "alert" },
  attachment_erased: { title: "Attachment erased.", treatment: "alert" },
  balance_outstanding: {
    title: "The host still owes part of the payment.",
    treatment: "finding",
  },
  bad_cursor: {
    title: "That page of results is no longer valid. Reload the list.",
    treatment: "alert",
  },
  bad_sort: { title: "That sort is not available.", treatment: "alert" },
  bad_transition: { title: "Bad transition.", treatment: "alert" },
  beo_not_ready: { title: "Beo not ready.", treatment: "alert" },
  // The six payout blockers. They arrive both from `GET /payouts/{id}` and inside a 409
  // `payout_blocked`, and are worded once here so both read the same.
  booking_not_confirmed: {
    title: "The vendor's booking is not confirmed yet.",
    treatment: "finding",
  },
  booking_exists: { title: "Booking exists.", treatment: "alert" },
  booking_invalid: { title: "Booking invalid.", treatment: "alert" },
  brief_invalid: {
    title: "Some brief values are not valid. Fix the fields marked below.",
    treatment: "field",
  },
  cancellation_terms: {
    title: "This package's cancellation terms cannot be read, so no tier refund can be worked out.",
    treatment: "alert",
  },
  candidate_exists: { title: "Candidate exists.", treatment: "alert" },
  candidate_removed: { title: "Candidate removed.", treatment: "alert" },
  cannot_demote_self: { title: "You cannot remove your own admin role.", treatment: "alert" },
  cannot_send_back: { title: "Cannot send back.", treatment: "alert" },
  category_mismatch: { title: "Category mismatch.", treatment: "alert" },
  category_required: { title: "Category required.", treatment: "field" },
  change_order_open: {
    title: "A newer version of the package is waiting for the host.",
    treatment: "finding",
  },
  config_invalid: {
    title: "This configuration is not valid. Fix the fields below.",
    treatment: "finding",
  },
  config_stale: {
    title: "Someone else changed this configuration while you were working.",
    treatment: "banner",
  },
  config_not_found: { title: "That configuration does not exist.", treatment: "alert" },
  confirmation_details_missing: { title: "Confirmation details missing.", treatment: "field" },
  description_required: { title: "Describe what went wrong.", treatment: "field" },
  dispute_closed: { title: "This problem has already been settled.", treatment: "alert" },
  dispute_window_open: {
    title: "The dispute window has not closed yet.",
    treatment: "finding",
  },
  money_invariant: {
    title: "The money on this event does not add up.",
    treatment: "finding",
  },
  payment_disputed: {
    title: "This event's payment is disputed, so nothing goes out.",
    treatment: "finding",
  },
  payment_refunded: {
    title: "This event's payment was refunded, so there is nothing to send.",
    treatment: "finding",
  },
  vendor_changed: {
    title: "Someone changed this vendor while you were editing. Reload and try again.",
    treatment: "alert",
  },
  vendor_matching_active: {
    title:
      "The vendor agents are matching this event. Pause vendor matching to change the shortlist yourself.",
    treatment: "alert",
  },
  vendor_matching_not_paused: {
    title: "Vendor matching is not paused on this event.",
    treatment: "alert",
  },
  vendor_matching_paused: {
    title: "Vendor matching is already paused on this event.",
    treatment: "alert",
  },
  vendor_not_payout_ready: {
    title:
      "The vendor cannot receive money yet: their Stripe account is not ready. Finish onboarding, or set them to be paid outside Stripe.",
    treatment: "finding",
  },
  vendor_paid_manually: {
    title: "This vendor is paid outside Stripe. Use Mark paid outside Stripe instead.",
    treatment: "alert",
  },
  paid_on_in_future: { title: "The date paid cannot be in the future.", treatment: "field" },
  dispute_open: {
    title: "A problem is open against this vendor, so their money stays held.",
    treatment: "alert",
  },
  confirmation_mismatch: {
    title: "That is not the event reference.",
    treatment: "alert",
  },
  force_delete_disabled: {
    title:
      "Deleting events is switched off. A Reviewer/Admin can switch it on in Configuration → Switches.",
    treatment: "alert",
  },
  event_has_payments: {
    title: "Money was taken on this event, so it cannot be deleted.",
    treatment: "alert",
  },
  deadline_after_quote_expiry: {
    title: "The deadline can't be later than the earliest quote expiry.",
    treatment: "field",
  },
  deadline_not_later: {
    title: "A published package's deadline can only move later.",
    treatment: "field",
  },
  draft_exists: { title: "Draft exists.", treatment: "alert" },
  draft_id_reused: {
    title: "This draft was already sent with different text.",
    treatment: "banner",
  },
  bad_timeline_type: {
    title: "That filter is not one the timeline knows.",
    treatment: "alert",
  },
  decline_not_sent: {
    title: "The decline could not be sent. Check the host's address and try again.",
    treatment: "alert",
  },
  rule_no_longer_applies: {
    title: "This rule no longer applies to the event, so there is nothing to send.",
    treatment: "alert",
  },
  fixed_in_required: {
    title: "Say which commit or release fixed it.",
    treatment: "field",
  },
  flag_not_closed_by_reply: {
    title: "A reply to the host does not settle this item. Decide it on its own.",
    treatment: "alert",
  },
  guidance_not_editable: {
    title:
      "This comes from an exception or a decision. Take the exception back, or it ends with the next email.",
    treatment: "alert",
  },
  incident_closed: {
    title: "This incident is already closed.",
    treatment: "alert",
  },
  draft_not_pending: {
    title: "This drafted reply has already been handled.",
    treatment: "alert",
  },
  draft_stale: {
    title: "The event changed since this reply was drafted. Dismiss it and reply by hand.",
    treatment: "alert",
  },
  duplicate_slot: { title: "Duplicate slot.", treatment: "alert" },
  email_exists: { title: "A user with that email already exists.", treatment: "field" },
  event_archived: { title: "This event is archived, so it cannot be changed.", treatment: "alert" },
  event_passed: {
    title: "The event date has passed, so the cancellation terms no longer apply.",
    treatment: "alert",
  },
  exceeds_payment: {
    title: "That is more than has been paid and not already refunded.",
    treatment: "field",
  },
  exception_not_approved: {
    title: "The manual payment exception has not been approved yet.",
    treatment: "alert",
  },
  exception_not_needed: {
    title: "That rule does not stop this event any more, so there is nothing to waive.",
    treatment: "alert",
  },
  feedback_done: { title: "Thank you, your feedback was already sent.", treatment: "page" },
  feedback_expired: { title: "This feedback link has expired.", treatment: "page" },
  forbidden: { title: "Your role does not allow this.", treatment: "alert" },
  gate_blocked: { title: "The checks for this step are not met yet.", treatment: "finding" },
  host_identity_leak: {
    title: "This would reveal the host's identity to the vendor. Remove it and try again.",
    treatment: "alert",
  },
  host_identity_leak_warning: {
    title: "This may reveal the host's identity to the vendor.",
    treatment: "finding",
  },
  "idempotency.send_ceiling": {
    title: "This address has reached its sending limit for now.",
    treatment: "finding",
  },
  "identity_claim.unacknowledged": {
    title: "This text may claim to be human or invent a history. Check it below.",
    treatment: "finding",
  },
  impact_closed: { title: "Someone already closed this change impact.", treatment: "alert" },
  invalid_credentials: { title: "That email, password or code is not right.", treatment: "field" },
  invalid_roles: { title: "Choose at least one valid role.", treatment: "field" },
  job_not_cancellable: {
    title: "Only a dead job can be cancelled.",
    treatment: "alert",
  },
  job_not_retryable: { title: "Job not retryable.", treatment: "alert" },
  last_admin: { title: "There must be at least one active admin.", treatment: "alert" },
  leaks_identity: { title: "Leaks identity.", treatment: "alert" },
  invalid_email: { title: "Check the email address.", treatment: "field" },
  invalid_city: { title: "Check the city.", treatment: "field" },
  invalid_status: { title: "Unknown status.", treatment: "alert" },
  nothing_selected: { title: "Choose sign-ups or a time range.", treatment: "field" },
  bad_range: { title: "The end must be after the start.", treatment: "field" },
  too_many_ids: { title: "Too many at once.", treatment: "alert" },
  no_brief: { title: "No brief.", treatment: "alert" },
  no_change: { title: "Nothing was changed, so nothing was saved.", treatment: "alert" },
  no_draft: { title: "No draft.", treatment: "alert" },
  no_event_date: {
    title: "The event has no date, so the cancellation terms cannot be applied.",
    treatment: "alert",
  },
  no_items: { title: "No items.", treatment: "alert" },
  no_payment: { title: "Nothing has been paid for this event yet.", treatment: "alert" },
  no_rfq_template: { title: "No rfq template.", treatment: "alert" },
  not_approvable: {
    title: "This is not waiting for approval any more. Reload to see where it got to.",
    treatment: "alert",
  },
  not_authenticated: {
    title: "Your session has ended. Sign in again to continue.",
    treatment: "page",
  },
  not_refundable_outside_stripe: {
    title:
      "Only a refund waiting to be paid back outside Stripe can be recorded. Reload to see where it got to.",
    treatment: "alert",
  },
  refunded_on_in_future: {
    title: "The date refunded cannot be in the future.",
    treatment: "field",
  },
  not_confirmed: { title: "Not confirmed.", treatment: "alert" },
  not_eligible: { title: "Not eligible.", treatment: "alert" },
  not_held: { title: "This payout is not on hold.", treatment: "alert" },
  not_holdable: {
    title: "This payout has gone too far to be held. Reload to see its state.",
    treatment: "alert",
  },
  not_linkable: {
    title: "Only an unmerged inbox thread can be linked to an RFQ.",
    treatment: "alert",
  },
  not_mergeable: { title: "This thread cannot be merged.", treatment: "alert" },
  not_paused: { title: "Not paused.", treatment: "alert" },
  not_rejectable: {
    title: "Only a refund still waiting for a decision can be rejected.",
    treatment: "alert",
  },
  not_published: { title: "Publish the package first.", treatment: "alert" },
  not_reviewer_admin: {
    title: "Only Reviewer/Admin accounts use an authenticator.",
    treatment: "alert",
  },
  note_required: { title: "A note is required.", treatment: "field" },
  note_has_contact_details: {
    title:
      "The agents can't take an email address or phone number. Leave it out, or keep the note staff only.",
    treatment: "alert",
  },
  nothing_refundable: {
    title: "The cancellation terms refund nothing at this date.",
    treatment: "alert",
  },
  nothing_to_publish: { title: "Nothing to publish.", treatment: "alert" },
  nothing_to_rate: {
    title: "There is no completed booking to ask about yet.",
    treatment: "alert",
  },
  // Both reach the host's acceptance page, so both are worded for a host, not for staff.
  option_required: { title: "Choose an option first.", treatment: "field" },
  option_unknown: {
    title: "That option is no longer available. Reload the page.",
    treatment: "field",
  },
  outbox_not_cancellable: {
    title: "Only a dead send can be cancelled.",
    treatment: "alert",
  },
  outbox_not_retryable: { title: "Outbox not retryable.", treatment: "alert" },
  package_expired: {
    title: "This package has expired. Ask us for a refreshed one.",
    treatment: "page",
  },
  package_superseded: {
    title: "A newer version of this package is available. Please use the latest link we sent.",
    treatment: "page",
  },
  amount_out_of_range: {
    title: "The amount must be at least one cent and no more than what is still owed.",
    treatment: "field",
  },
  received_in_future: { title: "The date received cannot be in the future.", treatment: "field" },
  nothing_owed: { title: "Nothing is owed on this payment.", treatment: "alert" },
  receipt_voided: { title: "This receipt is already void.", treatment: "alert" },
  receipt_not_voidable: {
    title: "This payment is refunded or disputed, so its receipts cannot be voided.",
    treatment: "alert",
  },
  receipt_funds_payouts: {
    title:
      "Money already paid out (or our fee, once booked) depends on this receipt, so it cannot be voided.",
    treatment: "alert",
  },
  due_after_event: { title: "The balance cannot be due after the event.", treatment: "field" },
  // Stripe invoices for a host's manual-payment request (manual-payment-request-plan.md slice C).
  not_invoice_request: {
    title: "This is not a host's request to pay by invoice.",
    treatment: "alert",
  },
  request_not_pending: { title: "This request has already been decided.", treatment: "alert" },
  due_on_invalid: {
    title: "Pick a due date after today and no later than the earliest quote expiry.",
    treatment: "field",
  },
  invoice_text_leak: {
    title: "An invoice line would reveal a provider. Fix the package item's words first.",
    treatment: "alert",
  },
  invoice_email_blocked: {
    title: "The invoice email would be blocked by its checks, so nothing was sent.",
    treatment: "alert",
  },
  invoices_unavailable: {
    title: "Invoices need live Stripe, which is not switched on here.",
    treatment: "alert",
  },
  invoice_refused: {
    title: "Stripe refused the invoice. Nothing was approved or sent.",
    treatment: "alert",
  },
  invoice_not_open: { title: "That invoice is no longer open.", treatment: "alert" },
  receipt_not_invoice_amount: {
    title: "A payment against an invoice must be the invoice's full amount.",
    treatment: "field",
  },
  receipt_settles_invoice: {
    title: "This receipt marked a Stripe invoice paid outside Stripe, which can't be undone.",
    treatment: "alert",
  },
  invoice_approval_required: {
    title: "Approve this request with its invoice.",
    treatment: "alert",
  },
  pricing_mismatch: {
    title: "The package's lines do not add up to its total. Republish the package.",
    treatment: "alert",
  },
  part_payment_below_deposits: {
    title: "The host has not yet paid every provider deposit plus our fee.",
    treatment: "finding",
  },
  balance_not_paid: {
    title: "The host still owes part of the payment, so balances wait.",
    treatment: "finding",
  },
  payments_unavailable: {
    title: "Payments are unavailable right now. Nothing was charged; try again shortly.",
    treatment: "alert",
  },
  payout_blocked: {
    title: "This payout cannot go out yet. See what is blocking it.",
    treatment: "finding",
  },
  pricing_error: { title: "Pricing error.", treatment: "alert" },
  proposal_invalid: {
    title: "That is not a manual payment exception for this event.",
    treatment: "alert",
  },
  proposal_not_pending: { title: "Proposal not pending.", treatment: "alert" },
  flag_has_parked_draft: {
    title:
      "A drafted reply is waiting for this flag. Send or dismiss it on the event's Conversation tab; that closes the flag.",
    treatment: "alert",
  },
  proposal_stale: {
    title: "This proposal changed while you were working. Reload to see the latest.",
    treatment: "banner",
  },
  publish_blocked: {
    title: "This package cannot be published yet. Fix the items below.",
    treatment: "finding",
  },
  question_already_settled: {
    title: "This question was settled since you ticked it. Nothing was changed.",
    treatment: "alert",
  },
  quote_already_expired: { title: "Quote already expired.", treatment: "alert" },
  quote_expired: {
    title:
      "A quote in this package has expired or needs the vendor to reconfirm it. Get it reconfirmed, then approve.",
    treatment: "alert",
  },
  quote_closed: {
    title: "This quote is closed, so it cannot take a new version.",
    treatment: "alert",
  },
  quote_exists: {
    title: "This RFQ already has a quote. Add a new version instead.",
    treatment: "alert",
  },
  quote_not_on_event: { title: "Quote not on event.", treatment: "alert" },
  quote_unavailable: { title: "Quote unavailable.", treatment: "alert" },
  rating_closed: { title: "Rating closed.", treatment: "alert" },
  ratings_required: { title: "Ratings required.", treatment: "field" },
  reason_invalid: { title: "That is not a reason you can ask for.", treatment: "field" },
  reason_required: { title: "Give a reason.", treatment: "field" },
  reference_required: {
    title: "A payment reference is required, up to 100 characters.",
    treatment: "field",
  },
  refund_failed: {
    title: "The refund did not go through, so the problem is still open.",
    treatment: "alert",
  },
  reply_now_answered: {
    title: "The host's latest message is already answered.",
    treatment: "alert",
  },
  reply_now_never_written: {
    title: "We have not written to the host yet, so there is nothing to follow up.",
    treatment: "alert",
  },
  reply_now_archived: { title: "The event is archived.", treatment: "alert" },
  reply_now_paused: { title: "Resume the event first.", treatment: "alert" },
  reply_now_takeover: {
    title: "Return control first, or write the reply yourself.",
    treatment: "alert",
  },
  reply_now_awaiting_admin: {
    title: "Decide the open flag on this event first. Deciding it starts the reply.",
    treatment: "alert",
  },
  reply_now_writing: { title: "The agents are already writing a reply.", treatment: "alert" },
  reviewer_admin_required: {
    title: "Only a Reviewer/Admin can waive a rule for an event.",
    treatment: "alert",
  },
  request_open: { title: "A feedback link is already out for this event.", treatment: "alert" },
  resolution_invalid: { title: "Choose how to settle this.", treatment: "field" },
  resolved_by_stripe: {
    title: "A chargeback is decided by the card network, not here.",
    treatment: "alert",
  },
  review_closed: {
    title: "Someone already decided this. Reload to see the result.",
    treatment: "banner",
  },
  review_too_long: { title: "Review too long.", treatment: "field" },
  rfq_exists: { title: "Rfq exists.", treatment: "alert" },
  rfq_fields_missing: { title: "The RFQ is missing required fields.", treatment: "field" },
  rfq_not_draft: { title: "This RFQ has already been sent.", treatment: "alert" },
  terms_not_accepted: {
    title: "Please agree to the terms to continue.",
    treatment: "field",
  },
  deposit_invalid: { title: "A usual deposit is 0 to 100%.", treatment: "field" },
  email_in_use: {
    title: "Another host already uses this address. Merge the hosts instead.",
    treatment: "field",
  },
  host_erased: { title: "This host was erased.", treatment: "alert" },
  rfq_not_open: { title: "Send the RFQ before recording a quote.", treatment: "alert" },
  score_invalid: { title: "Score invalid.", treatment: "field" },
  self_approval: {
    title: "You cannot approve your own request. Ask another Reviewer/Admin.",
    treatment: "alert",
  },
  send_blocked: {
    title: "This message cannot be sent yet. Fix the items below.",
    treatment: "finding",
  },
  stage_too_early: {
    title: "Too early for this: it opens once the event reaches Vendor outreach.",
    treatment: "alert",
  },
  stale_record_version: {
    title: "This changed while you were working. Reload to see the latest.",
    treatment: "banner",
  },
  takeover_active: { title: "Takeover active.", treatment: "alert" },
  token_consumed: {
    title: "This package has already been accepted. Thank you.",
    treatment: "page",
  },
  token_invalid: {
    title: "This link is not valid. Check the email or ask us for a new one.",
    treatment: "page",
  },
  transition_already_pending: { title: "Transition already pending.", treatment: "alert" },
  user_not_found: { title: "That user no longer exists.", treatment: "alert" },
  vendor_no_email: { title: "This vendor has no email address on file.", treatment: "alert" },
  vendor_not_active: { title: "Vendor not active.", treatment: "alert" },
  vendor_not_found: { title: "That vendor no longer exists.", treatment: "alert" },
  vendor_not_in_package: { title: "Vendor not in package.", treatment: "alert" },
  vendor_not_on_event: {
    title: "That provider has no payout on this event.",
    treatment: "field",
  },
  vendor_required: { title: "Say which provider this is about.", treatment: "field" },
  vendors_required: { title: "Name at least one provider.", treatment: "field" },
  version_not_found: { title: "That version does not exist.", treatment: "alert" },
  window_open: {
    title: "The dispute window is still open, so it is too early to ask.",
    treatment: "alert",
  },
  workflow_cannot_accept: { title: "Workflow cannot accept.", treatment: "alert" },
  wrong_password: { title: "Your current password is not right.", treatment: "field" },
  // Worded for a host: the only screen that meets this code is the public acceptance page, where
  // "wrong stage" is an internal term a host must never see (`ui-design.md` section 7).
  wrong_stage: {
    title: "This package is not ready to pay for right now. We will email you when it is.",
    treatment: "alert",
  },
  NotFound: {
    title: "That no longer exists. Reload the list.",
    treatment: "alert",
  },
  Conflict: {
    title: "That conflicts with the current state. Reload and try again.",
    treatment: "alert",
  },
  Invalid: { title: "Some of that is not valid.", treatment: "alert" },
  Forbidden: { title: "Your role does not allow this.", treatment: "alert" },
  Gone: { title: "That is no longer available.", treatment: "page" },
  BadGateway: { title: "A partner service refused the request.", treatment: "alert" },
  Unavailable: {
    title: "A partner service is unavailable. Nothing was sent; try again shortly.",
    treatment: "alert",
  },
  validation_error: { title: "Some fields are not valid.", treatment: "field" },
  network_error: {
    title: "The server could not be reached. Check your connection and try again.",
    treatment: "alert",
  },
  unknown_error: {
    title: "Something went wrong. Try again, and tell us if it keeps happening.",
    treatment: "alert",
  },
};

/** Codes that are prefixes: `template.*` findings and the like share one treatment. */
const PREFIXES: Record<string, CodeInfo> = {
  "template.": { title: "This text does not meet the template rules.", treatment: "finding" },
  "leakage.": {
    title: "This text would reveal something hosts or vendors must not see.",
    treatment: "finding",
  },
};

export function codeInfo(code: string): CodeInfo {
  if (code in CODES) return CODES[code];
  for (const [prefix, info] of Object.entries(PREFIXES)) if (code.startsWith(prefix)) return info;
  return CODES.unknown_error;
}
